<!--
  模块名称：本地 SVG 图标
  组件作用：按图标名称渲染本地 SVG 资源，并支持原色或继承色两种展示方式。
  组件用法：通过 name 指定图标，inherit-color=false 时保留 SVG 原始颜色。
-->
<template>
  <span
    class="local-svg-icon"
    :class="{ 'local-svg-icon-original-color': !inheritColor }"
    :aria-label="alt"
    :aria-hidden="alt ? 'false' : 'true'"
    role="img"
    v-bind="$attrs"
    v-on="$listeners"
  >
    <img
      v-if="iconSource.type === 'image'"
      class="local-svg-icon-image"
      :src="iconSource.url"
      alt=""
      draggable="false"
    />
    <svg
      v-else-if="spriteReady"
      class="local-svg-icon-svg"
      focusable="false"
      :aria-hidden="alt ? 'false' : 'true'"
    >
      <!-- 本地图标统一从 comment-actions-sprite.svg 读取 symbol，避免每个图标单独发起 SVG 请求。 -->
      <use :href="iconSource.href" :xlink:href="iconSource.href"></use>
    </svg>
  </span>
</template>

<script>
import { ensureCommentActionSprite, getCommentActionIconSource } from "@/assets/icon/icons.js";

export default {
  name: "LocalSvgIcon",
  inheritAttrs: false,
  props: {
    name: {
      type: String,
      default: "",
    },
    // 页面模块已有的本地 SVG 可以通过 src 复用此组件，无需先并入公共 sprite。
    src: {
      type: String,
      default: "",
    },
    alt: {
      type: String,
      default: "",
    },
    inheritColor: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      spriteReady: false,
    };
  },
  computed: {
    iconSource() {
      // 显式传入本地 SVG 时直接由组件渲染；未传入时继续按名称读取公共 sprite。
      if (this.src) {
        return {
          type: "image",
          url: this.src,
        };
      }

      // 普通图标返回 sprite href，大体积授权图标返回按需图片 URL。
      return getCommentActionIconSource(this.name, this.inheritColor);
    },
  },
  watch: {
    iconSource: {
      immediate: true,
      handler(source) {
        // sprite 图标只加载一次公共 sprite，注入 DOM 后再渲染 use，避免外链 currentColor 变黑。
        if (source.type !== "sprite") {
          this.spriteReady = true;
          return;
        }
        this.loadSprite();
      },
    },
  },
  methods: {
    async loadSprite() {
      this.spriteReady = false;
      try {
        await ensureCommentActionSprite();
        this.spriteReady = true;
      } catch (error) {
        // 精灵图短暂加载失败时保留空位，避免未处理的 Promise 异常影响页面其它交互。
        this.spriteReady = false;
      }
    },
  },
};
</script>

<style scoped>
.local-svg-icon {
  /* 本地 SVG 以内联方式渲染，颜色默认继承父元素 color。 */
  width: 16px;
  height: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: inherit;
  line-height: 1;
  vertical-align: -0.15em;
}

.local-svg-icon :deep(svg) {
  /* 内部 svg 填满组件尺寸，外部可通过 class/style 控制大小。 */
  width: 100%;
  height: 100%;
  display: block;
}

.local-svg-icon-svg {
  /* sprite 通过 use 复用 symbol，颜色由 symbol 内 currentColor 或原始颜色决定。 */
  color: inherit;
}

.local-svg-icon-image {
  /* 大体积图片图标保持按需 img 渲染，避免进入公共 sprite。 */
  width: 100%;
  height: 100%;
  display: block;
  object-fit: contain;
}

.local-svg-icon:not(.local-svg-icon-original-color) :deep([fill]:not([fill="none"])) {
  /* 兜底覆盖固定 fill 颜色，让填充色继承父级 color。 */
  fill: currentColor;
}

.local-svg-icon:not(.local-svg-icon-original-color) :deep([stroke]) {
  /* 兜底覆盖固定 stroke 颜色，让描边色继承父级 color。 */
  stroke: currentColor;
}
</style>
